import { useQuery } from '@tanstack/react-query';

import { api } from '../../services/client';
import { IBackofficeAdmin, IAuditLog, IAdminLoginLog } from '../../types';
import { createMutationHook } from './createMutation';

// --- Interfaces ---

export interface ICreateAdminData {
  username: string;
  displayName: string;
  password: string;
  permissions: string[];
  email?: string;
  mobileNumber?: string;
  countryCode?: string;
}

export interface IUpdateAdminData {
  displayName?: string;
  email?: string | null;
  mobileNumber?: string;
  countryCode?: string;
  password?: string;
  permissions?: string[];
  isActive?: boolean;
}

// --- Query Key Factory ---

export const adminKeys = {
  all: ['admins'] as const,
  lists: () => [...adminKeys.all, 'list'] as const,
  list: () => [...adminKeys.lists()] as const,
  details: () => [...adminKeys.all, 'detail'] as const,
  detail: (id: string) => [...adminKeys.details(), id] as const,
  auditLogs: (id: string) => [...adminKeys.detail(id), 'audit-logs'] as const,
  loginLogs: (id: string) => [...adminKeys.detail(id), 'login-logs'] as const,
};

// --- Query Hooks ---

export const useAdmins = () =>
  useQuery({
    queryKey: adminKeys.list(),
    queryFn: () => api.get<IBackofficeAdmin[]>('/backoffice/admins'),
  });

export const useAdminDetail = (id: string) => {
  return useQuery({
    queryKey: adminKeys.detail(id),
    queryFn: () =>
      api
        .get<IBackofficeAdmin[]>('/backoffice/admins')
        .then((list) => list.find((a) => a.id === id) ?? null),
    enabled: !!id,
  });
};

export const useAdminAuditLogs = (adminId: string) =>
  useQuery({
    queryKey: adminKeys.auditLogs(adminId),
    queryFn: () =>
      api
        .get<{
          data: IAuditLog[];
        }>(`/backoffice/audit-logs?adminId=${adminId}&limit=20`)
        .then((res) => res.data ?? []),
    enabled: !!adminId,
  });

export const useAdminLoginLogs = (adminId: string) =>
  useQuery({
    queryKey: adminKeys.loginLogs(adminId),
    queryFn: () =>
      api.get<IAdminLoginLog[]>(
        `/backoffice/admins/${adminId}/login-logs?limit=20`,
      ),
    enabled: !!adminId,
  });

// --- Mutation Hooks ---

export const useCreateAdmin = createMutationHook<
  IBackofficeAdmin,
  ICreateAdminData
>({
  mutationFn: (data) => api.post<IBackofficeAdmin>('/backoffice/admins', data),
  invalidateKeys: [adminKeys.lists()],
  successMessage: 'toast.admin_created',
  errorMessage: 'toast.admin_create_failed',
});

export const useUpdateAdmin = createMutationHook<
  IBackofficeAdmin,
  { id: string; data: IUpdateAdminData }
>({
  mutationFn: ({ id, data }) =>
    api.patch<IBackofficeAdmin>(`/backoffice/admins/${id}`, data),
  invalidateKeys: [adminKeys.all],
  successMessage: 'toast.admin_updated',
  errorMessage: 'toast.admin_update_failed',
});

export const useDeleteAdmin = createMutationHook<void, string>({
  mutationFn: (id) => api.delete<void>(`/backoffice/admins/${id}`),
  invalidateKeys: [adminKeys.lists()],
  successMessage: 'toast.admin_deleted',
  errorMessage: 'toast.admin_delete_failed',
});

export const useTransferOwnership = createMutationHook<void, string>({
  mutationFn: (adminId) =>
    api.post<void>(`/backoffice/admins/${adminId}/transfer-ownership`),
  invalidateKeys: [adminKeys.all],
  successMessage: 'toast.ownership_transferred',
  errorMessage: 'toast.ownership_transfer_failed',
});
